/**
 * Editable 9-channel × 12-dimension weight grid.
 *
 * DESIGN CHOICE: a plain MUI Table of controlled numeric inputs rather than MUI
 * X DataGrid. The matrix is a fixed, dense 108-cell grid where every cell is
 * simultaneously editable with the same 0–1 / 3-decimal rule; DataGrid's
 * click-to-edit-one-cell model fights that, and a controlled Table gives precise
 * validation + far simpler testing.
 *
 * Orientation: rows = channels, columns = dimensions — dimension display_names
 * (with a charter-sensitive badge) as column headers, per the task's readability
 * note. Horizontal scroll contains the 12 columns.
 *
 * Ported verbatim from quill-app/quill-admin's WeightMatrixGrid.tsx. Only the
 * type-source import and the "changed cell" highlight color changed — the
 * highlight now derives from the lifescribe theme's secondary (warmGold) token
 * via MUI's `alpha()` instead of a hardcoded rgba() literal.
 */

import React from 'react';
import {
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TextField,
  Tooltip,
  Box,
  Paper,
  Typography,
} from '@mui/material';
import { alpha, useTheme } from '@mui/material/styles';
import LockIcon from '@mui/icons-material/Lock';
import { WeightMatrix } from '../../../services/quillAdminService';
import { cellKey, humanizeChannel, channelsOf, normalizeWeight } from './matrixUtils';

interface Props {
  matrix: WeightMatrix;
  edits: Record<string, string>;
  onCellChange: (key: string, value: string) => void;
  disabled?: boolean;
}

const WeightMatrixGrid: React.FC<Props> = ({ matrix, edits, onCellChange, disabled }) => {
  const theme = useTheme();
  const channels = channelsOf(matrix);
  const baseMap = React.useMemo(() => {
    const m: Record<string, number> = {};
    matrix.cells.forEach((c) => {
      m[cellKey(c.channel, c.dimension_key)] = c.weight;
    });
    return m;
  }, [matrix]);

  return (
    <TableContainer component={Paper} sx={{ overflowX: 'auto' }}>
      <Table size="small" stickyHeader aria-label="weight matrix">
        <TableHead>
          <TableRow>
            <TableCell
              sx={{ position: 'sticky', left: 0, zIndex: 3, bgcolor: 'background.paper', fontWeight: 700 }}
            >
              Channel \ Dimension
            </TableCell>
            {matrix.dimensions.map((d) => (
              <TableCell key={d.dimension_key} align="center" sx={{ minWidth: 110 }}>
                <Box sx={{ display: 'flex', flexDirection: 'column', alignItems: 'center', gap: 0.5 }}>
                  <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
                    <Typography variant="caption" sx={{ fontWeight: 700, lineHeight: 1.1 }}>
                      {d.display_name}
                    </Typography>
                    {d.is_charter_sensitive && (
                      <Tooltip title="Charter-sensitive dimension — gated until member-confirmed">
                        <LockIcon
                          fontSize="inherit"
                          color="error"
                          aria-label={`${d.dimension_key} charter-sensitive`}
                          data-testid={`sensitive-${d.dimension_key}`}
                        />
                      </Tooltip>
                    )}
                  </Box>
                </Box>
              </TableCell>
            ))}
          </TableRow>
        </TableHead>
        <TableBody>
          {channels.map((channel) => (
            <TableRow key={channel} hover>
              <TableCell
                component="th"
                scope="row"
                sx={{ position: 'sticky', left: 0, zIndex: 2, bgcolor: 'background.paper', fontWeight: 600 }}
              >
                {humanizeChannel(channel)}
              </TableCell>
              {matrix.dimensions.map((d) => {
                const key = cellKey(channel, d.dimension_key);
                const original = baseMap[key];
                const value = edits[key] !== undefined ? edits[key] : original?.toFixed(3) ?? '';
                const parsed = normalizeWeight(value);
                const invalid = value !== '' && parsed === null;
                const changed = parsed !== null && parsed !== original;
                return (
                  <TableCell key={key} align="center" sx={{ p: 0.5 }}>
                    <TextField
                      type="number"
                      size="small"
                      value={value}
                      error={invalid}
                      disabled={disabled}
                      onChange={(e) => onCellChange(key, e.target.value)}
                      inputProps={{
                        min: 0,
                        max: 1,
                        step: 0.001,
                        'aria-label': `${channel} ${d.dimension_key} weight`,
                        style: { textAlign: 'center', padding: '6px 4px', width: 64 },
                      }}
                      sx={{
                        '& .MuiOutlinedInput-root': {
                          bgcolor: changed ? alpha(theme.palette.secondary.main, 0.14) : 'transparent',
                        },
                      }}
                    />
                  </TableCell>
                );
              })}
            </TableRow>
          ))}
        </TableBody>
      </Table>
    </TableContainer>
  );
};

export default WeightMatrixGrid;
